import { Button } from 'ui';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_WORD_LIMIT } from '../../../utils/wordCount';
import LessonSelectedCard from './components/LessonSelectedCard';
import LessonSelectionDialog from './components/LessonSelectionDialog';
import TextInputMode from './components/TextInputMode';
import useTextInputOrLessonSelector from './hooks/useTextInputOrLessonSelector';
import './textInputOrLessonSelector.scss';

const TextInputOrLessonSelector = React.forwardRef(function TextInputOrLessonSelectorComponent(
  {
    value = { mode: 'text', text: '', lesson: null, lessons: [] },
    onChange,
    wordLimit = DEFAULT_WORD_LIMIT,
    label,
    multiSelect = true,
    placeholder,
    required = false,
    disabled = false,
    showLabel = true,
    name = 'text-or-lesson-selector',
    defaultCourseId,
  },
  ref
) {
  const {
    mode,
    textValue,
    selectedLessons,
    isDialogOpen,
    wordCount,
    isOverLimit,
    courses,
    units,
    loadingUnits,
    loadingLessonContent,
    loadingCourses,
    contentError,
    handleTextChange,
    handleLessonsSelect,
    handleLessonSelect,
    handleRemoveLesson,
    openDialog,
    closeDialog,
    loadCourseUnits,
  } = useTextInputOrLessonSelector({ value, onChange, wordLimit, multiSelect });

  const { t, i18n } = useTranslation(['chatbot']);

  return (
    <div className="text-input-or-lesson-selector" ref={ref}>
      {showLabel && label && (
        <label className="text-input-or-lesson-selector__label label">
          {label}
          {required && <sup> *</sup>}
        </label>
      )}
      <div className="text-input-or-lesson-selector__content">
        {mode === 'text' ? (
          <TextInputMode
            disabled={disabled}
            error={isOverLimit}
            name={name}
            onChange={handleTextChange}
            onOpenDialog={openDialog}
            placeholder={placeholder || t('chatbot:Write, paste text or choose from your books')}
            value={textValue}
            wordCount={wordCount}
            wordLimit={wordLimit}
          />
        ) : !multiSelect ? (
          <LessonSelectedCard
            lesson={selectedLessons[0]}
            onRemove={handleRemoveLesson}
            disabled={disabled}
            isLoadingContent={loadingLessonContent}
            error={contentError}
          />
        ) : (
          <div className="text-input-or-lesson-selector__lessons-list">
            {selectedLessons.map((lesson) => (
              <LessonSelectedCard
                key={lesson.guid || lesson.lessonGuid}
                lesson={lesson}
                onRemove={handleRemoveLesson}
                disabled={disabled}
                isLoadingContent={loadingLessonContent}
                error={lesson.error || null}
              />
            ))}
            <div className="text-input-or-lesson-selector__add-more-container">
              {!disabled && (
                <div>
                  <Button
                    text={t('chatbot:Add more lessons')}
                    size="sm"
                    variant="primary-ghost"
                    onClick={openDialog}
                    className="text-input-or-lesson-selector__add-more"
                    isFullWidth
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {contentError && <div className="text-input-or-lesson-selector__error-message">{t('chatbot:teacher_tools_error_content_unavailable_message')}</div>}
        {isOverLimit && (
          <div className="text-input-or-lesson-selector__error-message">
            {t('chatbot:teacher_tools_word_limit_exceeded_message', { limit: wordLimit.toLocaleString(i18n.language) })}
          </div>
        )}
      </div>

      <LessonSelectionDialog
        open={isDialogOpen}
        onClose={closeDialog}
        onSelect={multiSelect ? handleLessonsSelect : handleLessonSelect}
        courses={courses}
        units={units}
        loadingUnits={loadingUnits}
        loadCourseUnits={loadCourseUnits}
        loadingCourses={loadingCourses}
        alreadySelectedLessons={selectedLessons}
        multiSelect={multiSelect}
        defaultCourseId={defaultCourseId}
      />
    </div>
  );
});

TextInputOrLessonSelector.propTypes = {
  value: PropTypes.shape({
    mode: PropTypes.oneOf(['text', 'lesson']),
    text: PropTypes.string,
    lesson: PropTypes.shape({
      guid: PropTypes.string,
      title: PropTypes.string,
      wordCount: PropTypes.number,
      courseName: PropTypes.string,
      unitName: PropTypes.string,
    }),
    lessons: PropTypes.arrayOf(
      PropTypes.shape({
        guid: PropTypes.string,
        title: PropTypes.string,
        wordCount: PropTypes.number,
        courseName: PropTypes.string,
        unitName: PropTypes.string,
      })
    ),
  }),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  multiSelect: PropTypes.bool,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  showLabel: PropTypes.bool,
  name: PropTypes.string,
  wordLimit: PropTypes.number,
  defaultCourseId: PropTypes.string,
};

export default TextInputOrLessonSelector;
